import { NavLink } from 'react-router-dom';
import styles from './nav.module.css';
import Routes from '../../app/routes';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import { close, selectMini, selectOpen } from './navSlice';
import {
  CalendarDaysIcon,
  ChartBarSquareIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  CloudArrowUpIcon,
  CurrencyPoundIcon,
  GiftIcon,
  SwatchIcon,
  TagIcon,
  UserIcon,
  WrenchScrewdriverIcon,
  RectangleStackIcon,
} from '@heroicons/react/24/outline';
import { useGetStagingStatusQuery } from '../staging/staging.api';
import { useGetEnquiriesCountQuery } from '../enquiries/enquiries.api';
import { useGetOrdersCountQuery } from '../orders/orders.api';
import { EnquiryStatus, OrderStatus } from '@topshots/core-models';

const NavBadge = ({ count, color }: { count: number; color: string }) => {
  if (count === 0) return null;
  return (
    <span
      style={{
        backgroundColor: color,
      }}
      className={styles.navBadge}
    >
      {count}
    </span>
  );
};

const StagingBadge = () => {
  const { data } = useGetStagingStatusQuery(undefined, {
    pollingInterval: 30000,
  });
  if (!data || data.fileCount === 0) return null;
  return <NavBadge count={data.fileCount} color="#83BD5E" />;
};

const EnquiriesBadge = () => {
  const { data } = useGetEnquiriesCountQuery(
    { status: EnquiryStatus.Open },
    { pollingInterval: 30000 }
  );
  if (!data || data.count === 0) return null;
  return <NavBadge count={data.count} color="#E5A54B" />;
};

const OrdersBadge = () => {
  const { data } = useGetOrdersCountQuery(
    { status: OrderStatus.Confirmed },
    { pollingInterval: 30000 }
  );
  if (!data || data.count === 0) return null;
  return <NavBadge count={data.count} color="#5B9FE5" />;
};

const badgeComponents: Record<string, () => JSX.Element | null> = {
  Staging: StagingBadge,
  Enquiries: EnquiriesBadge,
  Orders: OrdersBadge,
};

const NavItemBadge = ({ title }: { title: string }) => {
  const Badge = badgeComponents[title];
  return Badge ? <Badge /> : null;
};

export const Nav = () => {
  const open = useAppSelector(selectOpen);
  const mini = useAppSelector(selectMini);
  const dispatch = useAppDispatch();
  return (
    <>
      <div
        className={`${styles.mainNav__bg} ${
          open ? styles.mainNav__bg__open : ''
        }`}
        onClick={() => {
          dispatch(close());
        }}
      ></div>

      <nav
        className={`${styles.mainNav} ${open ? styles.mainNav__open : ''} ${
          mini ? styles.mainNav__mini : ''
        }`}
      >
        <div className={styles.header}>
          <svg
            style={{ maxWidth: '183px' }}
            width="401"
            height="49"
            viewBox="0 0 401 49"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M388.707 36.924C386.529 36.924 384.565 36.4368 382.817 35.4624C381.069 34.4879 379.693 33.0979 378.69 31.2924C377.716 29.4581 377.229 27.323 377.229 24.8869C377.229 22.4795 377.73 20.373 378.733 18.5674C379.736 16.7332 381.112 15.3432 382.86 14.3974C384.637 13.423 386.615 12.9358 388.793 12.9358C390.971 12.9358 392.934 13.423 394.682 14.3974C396.431 15.3432 397.792 16.7189 398.766 18.5244C399.769 20.33 400.271 22.4508 400.271 24.8869C400.271 27.323 399.769 29.4581 398.766 31.2924C397.763 33.0979 396.373 34.4879 394.596 35.4624C392.848 36.4368 390.885 36.924 388.707 36.924ZM388.707 35.2044C390.398 35.2044 391.974 34.8318 393.436 34.0867C394.926 33.3129 396.115 32.1521 397.004 30.6045C397.892 29.0569 398.336 27.151 398.336 24.8869C398.336 22.6514 397.892 20.7599 397.004 19.2123C396.115 17.6646 394.94 16.5183 393.479 15.7731C392.017 14.9993 390.441 14.6124 388.75 14.6124C387.059 14.6124 385.483 14.9993 384.021 15.7731C382.588 16.5183 381.427 17.6646 380.539 19.2123C379.65 20.7599 379.206 22.6514 379.206 24.8869C379.206 27.151 379.636 29.0569 380.496 30.6045C381.384 32.1521 382.545 33.3129 383.978 34.0867C385.44 34.8318 387.016 35.2044 388.707 35.2044Z"
              fill="white"
            />
            <path
              d="M366.53 18.0514C367.103 16.3318 368.121 15.0278 369.582 14.1393C371.044 13.2222 372.907 12.7637 375.171 12.7637V14.7842H374.526C372.234 14.7842 370.328 15.4434 368.809 16.7617C367.29 18.0514 366.53 20.1722 366.53 23.1242V36.5799H364.596V13.2366H366.53V18.0514Z"
              fill="white"
            />
            <path
              d="M359.399 14.9133C359.399 17.5787 358.567 19.6565 356.905 21.1468C355.243 22.6085 352.778 23.3393 349.511 23.3393H343.062V36.5801H341.128V6.40137H349.511C352.835 6.40137 355.315 7.17518 356.948 8.72281C358.582 10.2418 359.399 12.3053 359.399 14.9133ZM349.511 21.6197C354.813 21.6197 357.464 19.3842 357.464 14.9133C357.464 12.7065 356.833 11.0299 355.572 9.88352C354.311 8.70847 352.291 8.12095 349.511 8.12095H343.062V21.6197H349.511Z"
              fill="white"
            />
            <path
              d="M337.807 12.4629L322.503 48.0583H313.346L319.106 35.5483L309.219 12.4629H318.59L323.663 26.1336L328.564 12.4629H337.807Z"
              fill="white"
            />
            <path
              d="M299.844 16.719C300.79 15.3433 301.936 14.2542 303.283 13.4518C304.63 12.6493 306.077 12.248 307.625 12.248V21.2329H305.261C303.426 21.2329 302.065 21.5911 301.177 22.3076C300.288 23.0241 299.844 24.2708 299.844 26.0477V36.5802H291.418V12.463H299.844V16.719Z"
              fill="white"
            />
            <path
              d="M287.971 24.2419C287.971 24.9011 287.928 25.5603 287.842 26.2195H271.893C271.979 27.5378 272.337 28.5266 272.967 29.1858C273.627 29.8163 274.458 30.1315 275.461 30.1315C276.865 30.1315 277.868 29.501 278.47 28.24H287.455C287.082 29.9022 286.352 31.3926 285.263 32.7109C284.202 34.0006 282.855 35.018 281.221 35.7632C279.588 36.5083 277.782 36.8809 275.805 36.8809C273.426 36.8809 271.305 36.3794 269.442 35.3763C267.608 34.3732 266.161 32.9402 265.1 31.0773C264.069 29.2144 263.553 27.0219 263.553 24.4999C263.553 21.9778 264.069 19.7997 265.1 17.9655C266.132 16.1026 267.565 14.6696 269.399 13.6665C271.262 12.6634 273.397 12.1619 275.805 12.1619C278.184 12.1619 280.29 12.6491 282.124 13.6235C283.958 14.5979 285.391 16.0023 286.423 17.8365C287.455 19.6421 287.971 21.7772 287.971 24.2419ZM279.373 22.1355C279.373 21.1037 279.029 20.3012 278.341 19.728C277.653 19.1262 276.794 18.8253 275.762 18.8253C274.73 18.8253 273.885 19.1119 273.225 19.685C272.566 20.2296 272.136 21.0464 271.936 22.1355H279.373Z"
              fill="white"
            />
            <path
              d="M260.129 4.76782V36.5801H251.703V4.76782H260.129Z"
              fill="white"
            />
            <path
              d="M246.904 4.76782V36.5801H238.479V4.76782H246.904Z"
              fill="white"
            />
            <path
              d="M207.884 24.4999C207.884 22.0065 208.328 19.8283 209.216 17.9655C210.134 16.1026 211.366 14.6696 212.914 13.6665C214.49 12.6634 216.238 12.1619 218.158 12.1619C219.821 12.1619 221.254 12.4915 222.457 13.1506C223.661 13.8098 224.592 14.6983 225.252 15.816V12.4628H233.678V36.58H225.252V33.2268C224.592 34.3445 223.647 35.233 222.414 35.8921C221.211 36.5513 219.792 36.8809 218.158 36.8809C216.238 36.8809 214.49 36.3794 212.914 35.3763C211.366 34.3732 210.134 32.9402 209.216 31.0773C208.328 29.1858 207.884 26.9933 207.884 24.4999ZM225.252 24.4999C225.252 22.9523 224.822 21.7342 223.962 20.8458C223.131 19.9573 222.099 19.5131 220.867 19.5131C219.606 19.5131 218.56 19.9573 217.728 20.8458C216.897 21.7056 216.482 22.9236 216.482 24.4999C216.482 26.0475 216.897 27.2799 217.728 28.197C218.56 29.0854 219.606 29.5297 220.867 29.5297C222.099 29.5297 223.131 29.0854 223.962 28.197C224.822 27.3085 225.252 26.0762 225.252 24.4999Z"
              fill="white"
            />
            <path
              d="M196.069 16.246C195.582 15.4435 194.909 14.8273 194.049 14.3974C193.218 13.9675 192.229 13.7526 191.083 13.7526C188.962 13.7526 187.285 14.4404 186.053 15.8161C184.849 17.1917 184.247 19.0403 184.247 21.3617C184.247 23.9698 184.892 25.9616 186.182 27.3373C187.5 28.6843 189.406 29.3578 191.899 29.3578C194.851 29.3578 196.958 28.0251 198.219 25.3598H189.75V19.2982H205.484V27.4662C204.825 29.0712 203.851 30.5758 202.561 31.9802C201.3 33.3845 199.695 34.5452 197.746 35.4623C195.797 36.3508 193.576 36.795 191.083 36.795C188.045 36.795 185.351 36.1501 183.001 34.8605C180.679 33.5421 178.874 31.7222 177.584 29.4008C176.323 27.0507 175.692 24.371 175.692 21.3617C175.692 18.3811 176.323 15.7301 177.584 13.4086C178.874 11.0585 180.679 9.23865 183.001 7.94896C185.322 6.63061 188.002 5.97144 191.04 5.97144C194.851 5.97144 198.004 6.88855 200.497 8.72277C202.991 10.557 204.538 13.0647 205.14 16.246H196.069Z"
              fill="white"
            />
            <path
              d="M171.665 29.4007V36.58H168.011C161.849 36.58 158.768 33.5277 158.768 27.4232V19.4701H155.802V12.4628H158.768V6.61621H167.237V12.4628H171.622V19.4701H167.237V27.5522C167.237 28.2113 167.38 28.6842 167.667 28.9708C167.982 29.2574 168.498 29.4007 169.215 29.4007H171.665Z"
              fill="white"
            />
            <path
              d="M143.74 12.248C146.52 12.248 148.727 13.1795 150.36 15.0424C151.994 16.8766 152.811 19.37 152.811 22.5226V36.5802H144.385V23.6403C144.385 22.2646 144.027 21.1899 143.31 20.4161C142.594 19.6136 141.634 19.2124 140.43 19.2124C139.169 19.2124 138.18 19.6136 137.464 20.4161C136.747 21.1899 136.389 22.2646 136.389 23.6403V36.5802H127.963V12.463H136.389V15.9022C137.134 14.8131 138.137 13.939 139.398 13.2798C140.659 12.592 142.106 12.248 143.74 12.248Z"
              fill="white"
            />
            <path
              d="M124.516 24.2419C124.516 24.9011 124.473 25.5603 124.387 26.2195H108.438C108.524 27.5378 108.882 28.5266 109.512 29.1858C110.172 29.8163 111.003 30.1315 112.006 30.1315C113.41 30.1315 114.413 29.501 115.015 28.24H124C123.627 29.9022 122.896 31.3926 121.807 32.7109C120.747 34.0006 119.4 35.018 117.766 35.7632C116.133 36.5083 114.327 36.8809 112.35 36.8809C109.971 36.8809 107.85 36.3794 105.987 35.3763C104.153 34.3732 102.706 32.9402 101.645 31.0773C100.614 29.2144 100.098 27.0219 100.098 24.4999C100.098 21.9778 100.614 19.7997 101.645 17.9655C102.677 16.1026 104.11 14.6696 105.944 13.6665C107.807 12.6634 109.942 12.1619 112.35 12.1619C114.728 12.1619 116.835 12.6491 118.669 13.6235C120.503 14.5979 121.936 16.0023 122.968 17.8365C124 19.6421 124.516 21.7772 124.516 24.2419ZM115.918 22.1355C115.918 21.1037 115.574 20.3012 114.886 19.728C114.198 19.1262 113.338 18.8253 112.307 18.8253C111.275 18.8253 110.429 19.1119 109.77 19.685C109.111 20.2296 108.681 21.0464 108.481 22.1355H115.918Z"
              fill="white"
            />
            <path
              d="M85.1189 28.627L89.6328 12.4629H98.6176L90.3206 36.5801H79.8312L71.5342 12.4629H80.519L85.1189 28.627Z"
              fill="white"
            />
            <path
              d="M58.4289 13.0218V17.9656H68.1016V24.3711H58.4289V29.8308H69.3912V36.5801H50.0029V6.27246H69.3912V13.0218H58.4289Z"
              fill="white"
            />
            <rect
              x="21.417"
              y="0.000244141"
              width="12.9805"
              height="12.9805"
              transform="rotate(45 21.417 0.000244141)"
              fill="#00FFB2"
            />
            <path
              d="M33.6543 12.238L42.8329 21.4167L33.6543 30.5953L29.065 26.006L25.2405 29.8304L29.8299 34.4197L21.4161 42.8335L12.2375 33.6548L33.6543 12.238Z"
              fill="#00FFB2"
            />
            <rect
              x="9.17871"
              y="12.2385"
              width="12.9805"
              height="12.9805"
              transform="rotate(45 9.17871 12.2385)"
              fill="#00FFB2"
            />
          </svg>
        </div>
        <div className={styles.body}>
          <div className={styles.navSection}>
            {Object.values(Routes)
              .filter((i) => i.inMenu)
              .map((i) => (
                <NavLink
                  key={i.link}
                  title={i.title}
                  className={({ isActive }) =>
                    `${styles.navItem} ${
                      isActive ? styles.navItem__active : undefined
                    } ${mini ? styles.navItem__mini : undefined} ${
                      i.badged ? styles.navItem__badged : undefined
                    }`
                  }
                  to={i.link}
                >
                  {getIcon(i.title, 18)}
                  {mini ? null : i.title}
                  {!mini && <NavItemBadge title={i.title} />}
                </NavLink>
              ))}
          </div>
        </div>
      </nav>
    </>
  );
};

const getIcon = (title: string, size: number) => {
  switch (title) {
    case 'Dashboard':
      return <ChartBarSquareIcon height={size} />;
    case 'Enquiries':
      return <ChatBubbleOvalLeftEllipsisIcon height={size} />;
    case 'Coupons':
      return <TagIcon height={size} />;
    case 'Products':
      return <GiftIcon height={size} />;
    case 'Product Selectors':
      return <SwatchIcon height={size} />;
    case 'Orders':
      return <CurrencyPoundIcon height={size} />;
    case 'Events':
      return <CalendarDaysIcon height={size} />;
    case 'Users':
      return <UserIcon height={size} />;
    case 'Staging':
      return <CloudArrowUpIcon height={size} />;
    case 'Tasks':
      return <RectangleStackIcon height={size} />;
    case 'Settings':
      return <WrenchScrewdriverIcon height={size} />;
    default:
      return <ChartBarSquareIcon height={size} />;
  }
};

export default Nav;
